import { type EntityId, type SceneDocument } from '@/domain/model';

import { evaluateAll, evaluateFrom, type Scene } from './scene/Scene';
import { type SolveOptions } from './solver/levenbergMarquardt';
import { applyConstraints, type ClusterSolution, type DragAnchor } from './solver/solve';

/**
 * One complete update of the scene.
 *
 * This is where the two tiers of ADR-0003 compose, and the order is the whole point:
 *
 * 1. **Tier 1** re-evaluates the constructive DAG. Exact, one pass, no iteration.
 * 2. **Tier 2** solves any constraint cluster the change touched, moving only free points.
 * 3. **Tier 1 again**, seeded with whatever the solver moved, so everything derived from
 *    those points is exact once more.
 *
 * The second Tier-1 pass is not a detail. Without it, a midpoint whose endpoint the
 * solver nudged would hold a stale position until some unrelated edit happened to dirty
 * it — a class of bug that shows up as geometry that is *usually* right.
 *
 * There is deliberately no iteration between the tiers. Tier 2 can only write to free
 * points and Tier 1 only reads them, so a single round trip reaches a fixed point; a
 * loop would suggest the two can fight, which they cannot.
 */

export interface SceneUpdate {
  readonly document: SceneDocument;
  readonly scene: Scene;
  /** Every entity whose evaluated value changed, across both tiers. */
  readonly changed: ReadonlySet<EntityId>;
  /** Free points the constraint solver moved. Empty when there are no constraints. */
  readonly solverMoved: ReadonlySet<EntityId>;
  readonly solutions: readonly ClusterSolution[];
}

export interface UpdateOptions {
  /** Pins a dragged point towards the pointer while the constraints are solved. */
  readonly anchor?: DragAnchor | undefined;
  readonly solver?: SolveOptions | undefined;
}

/**
 * Applies a change to the document and returns the fully updated scene.
 *
 * `seeds` are the entities the caller edited. Pass an empty set to re-solve everything,
 * which is what loading a file does.
 */
export function updateScene(
  previous: Scene,
  document: SceneDocument,
  seeds: Iterable<EntityId>,
  options: UpdateOptions = {}
): SceneUpdate {
  const seedSet = new Set(seeds);

  const firstPass = evaluateFrom(previous, document, seedSet);
  const changed = new Set(firstPass.changed);

  const constraintPass = applyConstraints(
    document,
    firstPass.scene,
    seedSet.size === 0 ? firstPass.changed : seedSet,
    options.anchor,
    options.solver
  );

  if (constraintPass.moved.size === 0) {
    return {
      document: constraintPass.document,
      scene: firstPass.scene,
      changed,
      solverMoved: constraintPass.moved,
      solutions: constraintPass.solutions,
    };
  }

  const secondPass = evaluateFrom(firstPass.scene, constraintPass.document, constraintPass.moved);
  for (const id of secondPass.changed) changed.add(id);

  return {
    document: constraintPass.document,
    scene: secondPass.scene,
    changed,
    solverMoved: constraintPass.moved,
    solutions: constraintPass.solutions,
  };
}

/**
 * Builds a scene from scratch, solving every constraint.
 *
 * Used on load, where the file may contain constraints that were satisfied when it was
 * saved but no longer are — because the document was hand-edited, or because a migration
 * changed how something evaluates.
 */
export function initialiseScene(document: SceneDocument, options: UpdateOptions = {}): SceneUpdate {
  const base = evaluateAll(document);
  return updateScene(base, document, [], options);
}
